import { useState, useEffect, useCallback } from 'react';
import { C, COACHING_EMAIL, COACHING_PHONE, AIRVIEW_BASE } from '../config';
import { sendSMS, initiateCall, getPatientThread } from '../services/ringcentral';
import { Pill, UsageDots } from './Atoms';

function storedToken() {
  try { return JSON.parse(localStorage.getItem('sm_user') || '{}').token || null; }
  catch { return null; }
}

function generateBrighttreeNote(patient, commType, message, outcome, coachName) {
  const daysLeft = Math.max(0, 30 - patient.days);
  const useRate  = patient.days > 0 ? Math.round((patient.days4hr / patient.days) * 100) : 0;
  const time     = new Date().toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" });
  return `${new Date().toLocaleDateString("en-US")} ${time} — Sleep Coach Contact Note

Coach: ${coachName || "Sleep Metrics Coaching Team"}
Contact Method: ${commType}
Outcome: ${outcome}

PATIENT THERAPY STATUS (as of ${new Date().toLocaleDateString("en-US")}):
- Device: ${patient.device}
- Days on therapy: ${patient.days}
- Days used: ${patient.daysUsed} | Days 4hr: ${patient.days4hr}
- Usage rate: ${useRate}%${patient.days <= 30 ? "\n- Days remaining in compliance window: " + daysLeft : ""}
- Insurer: ${patient.insurer}

COMMUNICATION (${commType}):
${message}

OUTCOME: ${outcome}
————————————
Auto-generated - Sleep Metrics Compliance Dashboard
From: ${coachName}`;
}

function renderTemplate(body, patient) {
  const daysLeft = Math.max(0, 30 - patient.days);
  const useRate  = patient.days > 0 ? Math.round((patient.days4hr / patient.days) * 100) : 0;
  return body
    .replace(/{first}/g, patient.first).replace(/{last}/g, patient.last)
    .replace(/{days}/g, String(patient.days)).replace(/{days4hr}/g, String(patient.days4hr))
    .replace(/{daysUsed}/g, String(patient.daysUsed)).replace(/{daysLeft}/g, String(daysLeft))
    .replace(/{pct}/g, String(patient.pct)).replace(/{useRate}/g, String(useRate))
    .replace(/{device}/g, patient.device).replace(/{insurer}/g, patient.insurer);
}

function autoSelectTemplate(patient, templates) {
  if (!templates || templates.length === 0) return null;
  const match = templates.find(t => t.listType && patient.listType && t.listType === patient.listType);
  return match || templates[0];
}

function ContactModal({ patient, onClose, onSent, coachName, templates }) {
  const auto      = autoSelectTemplate(patient, templates);
  const [tab,     setTab]     = useState("sms");
  const [tmplId,  setTmplId]  = useState(auto?.id || templates[0]?.id);
  const [message, setMessage] = useState(() => auto ? renderTemplate(auto.body, patient) : "");
  const [outcome, setOutcome] = useState("Left voicemail");
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [copied,  setCopied]  = useState(false);
  const [btSent,  setBtSent]  = useState(false);
  const [btError, setBtError] = useState(null);   // Brightree push error detail
  const [blocked, setBlocked] = useState(null);   // gate reasonCode if a send was blocked
  const [thread,  setThread]  = useState(null);   // SMS history from outreach_events

  // Load the patient's SMS thread when the modal opens.
  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    getPatientThread(patient.id, storedToken())
      .then(t => { if (!cancelled) setThread(t); })
      .catch(() => { if (!cancelled) setThread([]); });
    return () => { cancelled = true; };
  }, [patient?.id]);

  const selectTemplate = (id) => {
    setTmplId(id);
    const t = templates.find(t => t.id === id);
    if (t) setMessage(renderTemplate(t.body, patient));
  };

  const commType = tab === "sms" ? "SMS" : tab === "call" ? "Phone Call" : "Email";
  const note     = generateBrighttreeNote(patient, commType, message, outcome, coachName);
  const daysLeft = Math.max(0, 30 - patient.days);
  const useRate  = patient.days > 0 ? Math.round((patient.days4hr / patient.days) * 100) : 0;

  // Safety gate: phone outreach (SMS/Call) is blocked until the patient has
  // granted SMS consent and is not suppressed. This is the interim client-side
  // guard until the server-side outreach gate (cooldown/STOP/logging) exists;
  // it enforces the locked decision that consent gates automation. Consent
  // defaults to 'unknown' for every imported patient, so this blocks by default.
  const consent = patient.smsConsent || 'unknown';
  const contactBlocked = (tab === "sms" || tab === "call") && (consent !== 'granted' || patient.suppressed);

  const handleSend = async () => {
    if (contactBlocked) return;               // never send to a non-consented patient
    setSending(true);
    setBlocked(null);
    try {
      if (tab === "sms") {
        const r = await sendSMS(patient.phone, message, { patientId: patient.id, coach: coachName });
        if (r.blocked) { setBlocked(r.reasonCode); setSending(false); return; }  // server gate said no
        if (r.ok) { setSent(true); onSent(patient.id, { type:commType, message, outcome, note }); }
      } else {
        const r = await initiateCall(COACHING_PHONE, patient.phone);
        if (r.ok || r.success) { setSent(true); onSent(patient.id, { type:commType, message, outcome, note }); }
      }
    } catch (e) {
      setBlocked('error');
    }
    setSending(false);
  };

  const copyNote = () => { navigator.clipboard.writeText(note); setCopied(true); setTimeout(()=>setCopied(false), 2200); };

  // Push the note to Brightree via the agent (server resolves the chart by MRN /
  // name+DOB and verifies identity before writing).
  const pushToBrightree = async () => {
    setBtSent("loading");
    setBtError(null);
    try {
      const res = await fetch('/api/brightree/note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${storedToken()}` },
        body: JSON.stringify({
          mrn: patient.mrn, lastName: patient.last, firstName: patient.first, dob: patient.dob,
          subject: `Sleep Coach Contact — ${patient.first} ${patient.last}`.slice(0, 100),
          description: note, coachName,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setBtSent("done");
      else { setBtSent("error"); setBtError(data.error || `Push failed (${res.status})`); }
    } catch (e) { setBtSent("error"); setBtError(e.message); }
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000030",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:1000, backdropFilter:"blur(3px)", padding:20,
    }} onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{
        background:C.surface, border:`1px solid ${C.border}`,
        borderRadius:16, width:"100%", maxWidth:720,
        boxShadow:"0 24px 60px #00000022", overflow:"hidden",
        maxHeight:"92vh", display:"flex", flexDirection:"column",
      }}>
        {/* Header */}
        <div style={{ padding:"16px 22px", borderBottom:`1px solid ${C.border}`, background:C.card, display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexShrink:0 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:C.text }}>{patient.first} {patient.last}</div>
            <div style={{ display:"flex", gap:7, marginTop:6, flexWrap:"wrap" }}>
              <Pill label={`Day ${patient.days}`} color={C.blue} />
              <Pill label={`${patient.days4hr}/${patient.days} nights ≥4hr`} color={useRate>=70?C.green:C.orange} />
              {patient.days<=30 && <Pill label={`${daysLeft}d left`} color={daysLeft<=5?C.red:C.yellow} />}
              <Pill label={patient.device} color={C.muted} />
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:20, cursor:"pointer", lineHeight:1 }}>✕</button>
        </div>

        {/* Quick links */}
        <div style={{ padding:"9px 22px", borderBottom:`1px solid ${C.border}`, background:"#EEF0F7", display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", flexShrink:0 }}>
          <span style={{ fontSize:11, color:C.muted }}>Quick links:</span>
          <a href={`${AIRVIEW_BASE}/${patient.ecoId || patient.airviewId}`} target="_blank" rel="noreferrer"
            style={{ fontSize:11, color:C.teal, background:C.teal+"12", border:`1px solid ${C.teal}28`, padding:"4px 11px", borderRadius:5, textDecoration:"none", fontWeight:600 }}>
            📊 AirView →</a>
          {patient.phone && <a href={`rcmobile://call?number=${patient.phone}`}
            style={{ fontSize:11, color:C.green, background:C.green+"12", border:`1px solid ${C.green}28`, padding:"4px 11px", borderRadius:5, textDecoration:"none", fontWeight:600 }}>
            📞 Dial →</a>}
          {patient.email && <span style={{ fontSize:11, color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>{patient.email}</span>}
        </div>

        {/* Comm tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, padding:"0 22px", flexShrink:0 }}>
          {[
            { id:"sms",   label:"💬 SMS",       disabled:!patient.phone },
            { id:"call",  label:"📞 Phone Call" },
            { id:"email", label:"📧 Email",      disabled:!patient.email },
          ].map(t => (
            <button key={t.id} onClick={() => !t.disabled && setTab(t.id)} style={{
              background:"none", border:"none",
              borderBottom: tab===t.id ? `2px solid ${C.teal}` : "2px solid transparent",
              color: tab===t.id ? C.text : t.disabled ? C.faint : C.muted,
              padding:"11px 16px", fontSize:12, fontWeight: tab===t.id ? 700 : 500,
              cursor: t.disabled ? "not-allowed" : "pointer",
              fontFamily:"inherit", marginBottom:-1,
            }}>{t.label}{t.disabled && tab!=="email" ? " (no phone)" : ""}</button>
          ))}
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY:"auto", flex:1 }}>
          <div style={{ padding:"16px 22px", display:"flex", flexDirection:"column", gap:13 }}>

            {/* EMAIL tab — full Gmail panel */}
            {tab === "email" ? (
              <EmailThreadPanel patient={patient} templates={templates} coachName={coachName} onSent={onSent} />
            ) : (
              <>
                {/* SMS thread history (from outreach_events) */}
                {tab === "sms" && (
                  <div>
                    <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" }}>💬 SMS History</div>
                    {thread === null ? (
                      <div style={{ fontSize:12, color:C.muted, padding:"8px 0" }}>Loading…</div>
                    ) : thread.length === 0 ? (
                      <div style={{ fontSize:12, color:C.muted, padding:"8px 12px", background:C.card, borderRadius:7, border:`1px solid ${C.border}` }}>No prior SMS with this patient.</div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:150, overflowY:"auto" }}>
                        {thread.map(m => {
                          const inbound = m.direction === "inbound";
                          const skipped = m.decision === "skipped";
                          return (
                            <div key={m.id} style={{
                              alignSelf: inbound ? "flex-start" : "flex-end", maxWidth:"85%",
                              padding:"7px 11px", borderRadius:9,
                              background: inbound ? C.card : skipped ? C.orange+"10" : C.teal+"0C",
                              border:`1px solid ${inbound ? C.border : skipped ? C.orange+"30" : C.teal+"28"}`,
                            }}>
                              <div style={{ fontSize:9, fontWeight:700, color: inbound ? C.blue : skipped ? C.orange : C.teal, marginBottom:2, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                                {inbound ? "Patient" : skipped ? `Not sent · ${m.reasonCode}` : (m.automated ? "Auto" : m.coach || "Coach")}
                              </div>
                              {m.body && <div style={{ fontSize:12, color:C.text, lineHeight:1.45 }}>{m.body}</div>}
                              <div style={{ fontSize:9, color:C.muted, marginTop:3 }}>{new Date(m.createdAt).toLocaleString()}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Template picker */}
                <div>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" }}>Template</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {templates.map(t => (
                      <button key={t.id} onClick={() => selectTemplate(t.id)} style={{
                        background: tmplId===t.id ? C.teal+"16" : C.card,
                        border:`1px solid ${tmplId===t.id ? C.teal+"50" : C.border}`,
                        color: tmplId===t.id ? C.teal : C.muted,
                        borderRadius:5, padding:"4px 10px", fontSize:11,
                        cursor:"pointer", fontFamily:"inherit", fontWeight:600,
                      }}>{t.label}</button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                    {tab==="sms" ? "SMS Message" : "Call Script / Notes"}
                  </div>
                  <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={4} style={{
                    width:"100%", background:C.card, border:`1px solid ${C.border}`,
                    color:C.text, borderRadius:8, padding:"11px 13px",
                    fontSize:13, fontFamily:"inherit", outline:"none", resize:"vertical", lineHeight:1.6,
                  }} />
                  {tab==="sms" && <div style={{ fontSize:10, color:message.length>160?C.orange:C.muted, marginTop:3, textAlign:"right" }}>{message.length} chars{message.length>160?" — multi-part":""}</div>}
                </div>

                {/* Outcome */}
                <div>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" }}>Outcome</div>
                  <select value={outcome} onChange={e=>setOutcome(e.target.value)} style={{
                    width:"100%", background:C.card, border:`1px solid ${C.border}`,
                    color:C.text, borderRadius:7, padding:"9px 12px",
                    fontSize:13, fontFamily:"'IBM Plex Mono',monospace", outline:"none", cursor:"pointer",
                  }}>
                    <option>Left voicemail</option>
                    <option>Patient answered — engaged positively</option>
                    <option>Patient answered — has concerns (mask fit)</option>
                    <option>Patient answered — has concerns (comfort/pressure)</option>
                    <option>Patient answered — stopping therapy</option>
                    <option>SMS sent — no reply yet</option>
                    <option>SMS sent — patient replied positively</option>
                    <option>No answer, no voicemail</option>
                    <option>Wrong number</option>
                    <option>Escalated to physician</option>
                  </select>
                </div>
              </>
            )}

            {/* Brightree note — always visible */}
            {tab !== "email" && (
              <div style={{ background:"#EEF0F7", border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden" }}>
                <div style={{ padding:"8px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em" }}>📋 Brightree Note</span>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={copyNote} style={{
                      background: copied ? C.green+"16" : C.surface,
                      border:`1px solid ${copied ? C.green+"40" : C.border}`,
                      color: copied ? C.green : C.muted,
                      borderRadius:5, padding:"4px 11px", fontSize:11,
                      cursor:"pointer", fontFamily:"inherit", fontWeight:700,
                    }}>{copied ? "✓ Copied!" : "Copy"}</button>
                    <button onClick={pushToBrightree} disabled={!sent || btSent==="loading" || btSent==="done"} style={{
                      background: btSent==="done" ? C.green+"16" : btSent==="loading" ? C.orange+"16" : btSent==="error" ? C.red+"12" : C.blue+"10",
                      border:`1px solid ${btSent==="done" ? C.green+"40" : btSent==="loading" ? C.orange+"40" : btSent==="error" ? C.red+"40" : C.blue+"28"}`,
                      color: btSent==="done" ? C.green : btSent==="loading" ? C.orange : btSent==="error" ? C.red : C.blue,
                      borderRadius:5, padding:"4px 11px", fontSize:11,
                      cursor: (!sent || btSent==="loading" || btSent==="done") ? "not-allowed" : "pointer",
                      fontFamily:"inherit", fontWeight:700,
                      opacity: !sent ? 0.4 : 1,
                    }}>
                      {btSent==="done" ? "✓ In Brightree" : btSent==="loading" ? "Writing…" : btSent==="error" ? "Retry Brightree" : "Push to Brightree"}
                    </button>
                  </div>
                </div>
                <pre style={{
                  margin:0, padding:"11px 14px", fontSize:11, color:C.muted,
                  fontFamily:"'IBM Plex Mono',monospace", lineHeight:1.7,
                  whiteSpace:"pre-wrap", maxHeight:150, overflowY:"auto",
                }}>{note}</pre>
                {btSent==="error" && btError && (
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}`, fontSize:11, color:C.red, background:C.red+"08" }}>
                    ⚠ Brightree: {btError}
                  </div>
                )}
              </div>
            )}

            {/* Consent gate notice — phone outreach blocked until consent granted */}
            {tab !== "email" && contactBlocked && (
              <div style={{ background:C.orange+"12", border:`1px solid ${C.orange}38`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.orange, lineHeight:1.5 }}>
                🔒 <strong>Outreach gated.</strong> SMS consent for this patient is <strong>{consent}</strong>{patient.suppressed ? " and they are suppressed (opted out)" : ""}.
                Sending is blocked until consent is <strong>granted</strong> and the outreach safety gate is live. You can still review the chart, copy the Brightree note, and log the outcome.
              </div>
            )}

            {/* Server safety-gate block (shown if a send was rejected server-side) */}
            {tab !== "email" && blocked && (
              <div style={{ background:C.red+"10", border:`1px solid ${C.red}38`, borderRadius:8, padding:"9px 13px", fontSize:12, color:C.red }}>
                🚫 Send blocked by the safety gate — <strong>{blocked}</strong>. Nothing was sent; the decision was logged.
              </div>
            )}

            {/* Send actions */}
            {tab !== "email" && (
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={onClose} style={{
                  background:"none", border:`1px solid ${C.border}`, color:C.muted,
                  borderRadius:8, padding:"10px 18px", fontSize:13, cursor:"pointer", fontFamily:"inherit", fontWeight:600,
                }}>Cancel</button>
                {!sent ? (
                  <button onClick={handleSend} disabled={sending||contactBlocked||(tab==="sms"&&!patient.phone)} style={{
                    background: contactBlocked ? C.faint : `linear-gradient(135deg, ${C.teal}, ${C.blue})`,
                    border:"none", color:"#fff", borderRadius:8, padding:"10px 22px",
                    fontSize:13, cursor:(contactBlocked)?"not-allowed":sending?"wait":"pointer", fontFamily:"inherit", fontWeight:700,
                    opacity:(contactBlocked||(tab==="sms"&&!patient.phone))?0.5:1,
                  }}>{contactBlocked ? "🔒 Consent required" : sending ? "Sending…" : tab==="sms" ? "Send SMS via RingCentral" : "Log Call + Copy Note"}</button>
                ) : (
                  <div style={{ background:C.green+"16", border:`1px solid ${C.green}38`, color:C.green, borderRadius:8, padding:"10px 22px", fontSize:13, fontWeight:700 }}>
                    ✓ Logged — push to Brightree or copy note
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactModal;
